import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../components/atoms/form/button";
import { Flex, Box } from "rebass/styled-components";
import { Body } from "../../../components/text";
import { LabeledInputText } from "../../../components/atoms/form/labeledInput";
import { SettingsContainer } from "../../../components/molecules/settingsContainer";
import { DividerAlt } from "../../../components/atoms/dividerAlt";
import { ReactComponent as YouTubeIcon } from "../../../assets/images/socials/youtube.svg";
import { getYoutube, deleteYoutube, patchYoutube } from "./dataHandler";
import { LabeledReadOnlyText } from "../../../components/atoms/form/labeledReadOnly";
import { HiOutlineX } from "react-icons/hi";
import Lottie from "lottie-react";
import Loading from "../../../animations/loading.json";
import { HiOutlinePlusCircle } from "react-icons/hi";
import styled from "styled-components";
import { Error } from "../../../components/atoms/form/error";
import { useTranslation } from "react-i18next";
import { analytics } from "MVVM/Analytics";
const lottieStyle = {
  height: 28,
  width: 28,
};

let RemoveWrapper = styled(Box)`
  position: absolute;
  right: 20px;
  top: 15px;
`;
let StyledX = styled(HiOutlineX)`
  color: ${(props) => {
    return props.theme.colors.chalk;
  }};
  &:hover {
    cursor: pointer;
    color: ${(props) => {
      return props.theme.colors.ice;
    }};
  }
`;

let BodyTitle = styled(Body)`
  margin-bottom: ${(props) => {
    return props.theme.space[2];
  }}px;

  color: ${(props) => {
    return props.theme.colors.ice;
  }};
`;
let StyledForm = styled.form`
  margin-top: ${(props) => {
    return props.theme.space[8];
  }}px;
`;

const ERROR_MESSAGES = {
  get: "Oh no! Something happened while retrieving your youtube information. Contact support on Discord.",
  save: "Oh no! Something happened while saving your youtube information. Contact support on Discord, or email us at support@allstar.gg.",
};

export function YoutubeLicense() {
  const {
    handleSubmit,
    register,
    formState,
    formState: { errors },
  } = useForm();
  const { t } = useTranslation(`translation`, {
    keyPrefix: "account.publish",
  });

  let [state, setState] = useState({
    loading: true,
    data: null,
    toRender: [],
    initialFetchDone: false,
    saveComplete: false,
    error: false,
    errorType: "",
  });
  let [validationError, setValidationError] = useState(false);
  let stateRef = useRef(state);
  let renderCountRef = useRef(0);

  useEffect(() => {
    if (state.saveComplete && state.initialFetchDone) {
      getYoutube(localStorage.getItem("token"), setState, state);
    }
    if (!state.initialFetchDone) {
      getYoutube(localStorage.getItem("token"), setState, state);
    }
  }, [state.saveComplete]); //eslint-disable-line

  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  //determines how many input boxes to render, and whether or not they are editable
  useEffect(() => {
    let inputsToRender = [];

    if (!state.error && state.data) {
      //renders the youtube ids we already have in the database
      state.data.forEach((id, index) => {
        let key = renderCountRef.current;
        inputsToRender.push(
          <LabeledReadOnlyText
            input={{
              id: `YouTube Channel ID-${key}`,
              name: `YouTube Channel ID-${key}`,
              "data-lpignore": true,
              ...register(`YouTube Channel ID-${key}`, {
                required: false,
              }),
            }}
            mt={6}
            value={id}
            isReadOnly={true}
            text={id}
            label={"YouTube Channel ID"}
            placeholder={id}
            key={key}
          >
            {" "}
            <RemoveWrapper
              onClick={() => {
                removeField(`${key}`);
              }}
            >
              <StyledX size="23px" />
            </RemoveWrapper>
          </LabeledReadOnlyText>,
        );

        renderCountRef.current++;
      });
      let key = renderCountRef.current;
      //renders 1 initial input field so the user can enter another id on first page land
      inputsToRender.push(
        <LabeledInputText
          input={{
            id: `YouTube Channel ID-${key}`,
            name: `${key}`,
            "data-lpignore": true,
            ...register(`YouTube Channel ID-${key}`, {
              required: false,
            }),
          }}
          label={"YouTube Channel ID"}
          placeholder="YouTube Channel ID"
          key={key}
        >
          {" "}
          <RemoveWrapper
            onClick={() => {
              removeField(`${key}`);
            }}
          >
            <StyledX size="23px" />
          </RemoveWrapper>
        </LabeledInputText>,
      );
      renderCountRef.current++;

      return setState({
        ...state,
        loading: false,
        toRender: inputsToRender,
        initialRenderContents: inputsToRender,
      });
    }
  }, [state.data]); //eslint-disable-line

  //Just FYI, I know we usually use the useForm errors to pass errors into our input fields,
  //but the way ive handled rendering the input boxes on this component requires us to use a ref
  //to allow the input fields callbacks to have access to a fresh state.
  //I tried to use a ref to maintain the error state, but was running into a lot of issues with that so thats why ive manually handled validation here
  const onSubmit = async (form) => {
    let errors = [];
    form = await extractDirtyFields(form);

    Object.keys(form).forEach((key) => {
      if (!isValidId(form[key])) {
        errors.push(key);
      }
    });
    if (errors.length) return whoHasError(errors);
    setValidationError(false);
    setState({ ...state, loading: true });
    analytics.track(`Youtube Alibi Whitelist - ID Addition`);
    await patchYoutube(
      Object.values(form),
      localStorage.getItem("token"),
      state,
      setState,
    );
  };

  function whoHasError(errors) {
    let rendered = [];

    state.toRender.forEach((component) => {
      if (errors.includes(component.key)) {
        let newComponent = React.cloneElement(component, { error: true });
        rendered.push(newComponent);
        setValidationError(true);
      } else {
        let newComponent = React.cloneElement(component, { error: false });
        rendered.push(newComponent);
      }
    });
    setState({ ...state, toRender: rendered });
    return;
  }

  function clearForm() {
    let clearedState = state.toRender;

    clearedState = clearedState.filter((box) => {
      return box.props.isReadOnly;
    });
    setValidationError(false);

    return setState({ ...state, toRender: clearedState });
  }

  function addField() {
    let fieldsToRender = state.toRender;
    let key = renderCountRef.current;
    fieldsToRender.push(
      <LabeledInputText
        input={{
          id: `YouTube Channel ID-${key}`,
          autoFocus: true,
          "data-lpignore": true,
          ...register(`YouTube Channel ID-${key}`, {
            required: false,
          }),
        }}
        label={"YouTube Channel ID"}
        placeholder="YouTube Channel ID"
        key={`${key}`}
      >
        <RemoveWrapper
          onClick={() => {
            removeField(`${key}`);
          }}
        >
          <StyledX size="23px" />
        </RemoveWrapper>
      </LabeledInputText>,
    );
    renderCountRef.current++;

    return setState({ ...state, toRender: fieldsToRender });
  }

  function removeField(key) {
    setState({ ...state, loading: true });

    let currComponent = stateRef.current.toRender.filter((component) => {
      return component.key === key.toString();
    })[0];
    analytics.track(`Youtube Alibi Whitelist - ID Removal`);
    if (currComponent.props.isReadOnly) {
      deleteYoutube(
        currComponent.props.text,
        localStorage.getItem("token"),
        state,
        setState,
      );
    } else {
      let removedField = stateRef.current.toRender.filter((component) => {
        return !(component.key === key.toString());
      });

      //check if there are errors after removing the field
      let foundError = false;
      removedField.forEach((component) => {
        foundError = component.props.error;
      });
      if (!foundError) setValidationError(false);

      return setState({ ...stateRef.current, toRender: removedField });
    }
  }

  function extractDirtyFields(form) {
    let dirty = {};
    Object.keys(form).forEach((key) => {
      if (formState.dirtyFields.hasOwnProperty(key)) {
        dirty[key] = form[key];
      }
    });
    return dirty;
  }

  function isValidId(id) {
    return /^[0-9A-Za-z_-]{24}$/.test(id);
  }

  return (
    <SettingsContainer title={t("musicLicensing")} mb={5}>
      <DividerAlt></DividerAlt>

      <YouTubeIcon />

      <BodyTitle mt={4}>{t("channelID")}</BodyTitle>
      <Body color="chalk" mb={6}>
        {t("channelIDbody")}
      </Body>
      <BodyTitle>{t("findID")}</BodyTitle>
      <Body color="chalk" mb={6}>
        {t("findIDbody")}
      </Body>
      <BodyTitle>{t("finePrint")}</BodyTitle>
      <Body color="chalk" mb={6}>
        {t("finePrintbody")}
      </Body>
      {state.loading ? (
        <Flex justifyContent={"center"}>
          <Lottie animationData={Loading} style={lottieStyle} loop={true} />
        </Flex>
      ) : (
        <>
          {state.error ? (
            <Flex>
              <Body mb={4} color="error">
                {ERROR_MESSAGES[state.errorType]}
              </Body>
            </Flex>
          ) : (
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
              {state.toRender.map((Component) => {
                return Component;
              })}
              {validationError ? (
                <Error>
                  {" "}
                  One or more of your YouTube channel IDs is invalid.
                </Error>
              ) : (
                <></>
              )}
              <Button
                mt={[3]}
                size="small"
                classification="secondary"
                type={"button"}
                onClick={() => addField()}
              >
                <Flex alignItems="center" justifyContent="center">
                  {" "}
                  <HiOutlinePlusCircle />
                  <Box ml={2}>{t("add")}</Box>
                </Flex>
              </Button>
              <Flex pt={[5]} justifyContent="flex-start">
                <Button
                  size="small"
                  flex={true}
                  mt={3}
                  type="submit"
                  disabled={!formState.isDirty}
                >
                  {t("saveProfile")}
                </Button>
                <Button
                  type="button"
                  size="small"
                  flex={true}
                  classification="subtle"
                  ml={4}
                  mt={3}
                  onClick={() => {
                    clearForm();
                  }}
                >
                  {t("cancel")}
                </Button>
              </Flex>
            </StyledForm>
          )}
        </>
      )}
    </SettingsContainer>
  );
}
